// v2.18.5 — Orchestrateur watchdog + toast embouteillage + log-reroute backend.
// - Lit la course active via /api/v3/courier/courses/active.
// - Si en cours : monte le watchdog. Sur trigger → si autoMode → accept silencieux, sinon → affiche toast.
// - Accept → POST log-reroute + dispatch event `civ-courier-reroute-applied` (pour la map) + toast info.
// - Reject → snooze watchdog 5 min + POST log-reroute (accepted:false).

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import useTrafficWatchdog from "@/hooks/useTrafficWatchdog";
import TrafficRerouteToast from "@/components/TrafficRerouteToast";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const AUTO_KEY = "civ_courier_pilote_auto";

function _readAuto() {
  try { return window.localStorage.getItem(AUTO_KEY) === "1"; } catch (_) { return false; }
}

export default function TrafficGuardian() {
  const [activeCourse, setActiveCourse] = useState(null);
  const [payload, setPayload] = useState(null);
  const [autoMode, setAutoMode] = useState(_readAuto());

  // Resume la course active au mount + refresh sur event "civ-courier-course-changed".
  const refreshActive = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/v3/courier/courses/active`, { withCredentials: true });
      if (r.data?.ok && r.data.active) setActiveCourse(r.data.active);
      else setActiveCourse(null);
    } catch (_) { setActiveCourse(null); }
  }, []);

  useEffect(() => { refreshActive(); }, [refreshActive]);
  useEffect(() => {
    const onChange = () => refreshActive();
    window.addEventListener("civ-courier-course-changed", onChange);
    return () => window.removeEventListener("civ-courier-course-changed", onChange);
  }, [refreshActive]);

  // Toggle auto mode (event venu du drawer).
  useEffect(() => {
    const onAutoToggle = (e) => setAutoMode(!!(e?.detail?.enabled));
    window.addEventListener("civ-courier-pilote-auto-changed", onAutoToggle);
    return () => window.removeEventListener("civ-courier-pilote-auto-changed", onAutoToggle);
  }, []);

  const destination = useMemo(() => {
    if (!activeCourse?.destination) return null;
    const { lat, lng } = activeCourse.destination;
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }, [activeCourse?.destination?.lat, activeCourse?.destination?.lng]);

  // Ref stable pour le callback onTrafficDetected (évite re-run du watchdog à chaque render).
  const payloadRef = useRef(null);
  const activeCourseRef = useRef(activeCourse);
  useEffect(() => { activeCourseRef.current = activeCourse; }, [activeCourse]);
  useEffect(() => { payloadRef.current = payload; }, [payload]);

  const acceptReroute = useCallback(async (mode = "manual", currentPayload = null) => {
    const p = currentPayload || payloadRef.current;
    const ac = activeCourseRef.current;
    if (!p || !ac) return;
    try {
      await axios.post(
        `${API}/v3/courier/courses/${ac.course_id}/log-reroute`,
        { saving_minutes: p.best.saving_minutes, accepted: true, source: mode },
        { withCredentials: true },
      );
      // Émet l'event que DeliveryHUD/Map peut écouter pour override la polyline.
      window.dispatchEvent(new CustomEvent("civ-courier-reroute-applied", {
        detail: { geometry: p.best.geometry, alt_id: p.best.id },
      }));
      if (mode === "auto") {
        toast(`🤖 Raccourci pris auto (-${p.best.saving_minutes} min)`);
      } else {
        toast.success(`✓ Raccourci appliqué (-${p.best.saving_minutes} min)`);
      }
    } catch (e) {
      toast.error("Log raccourci impossible.");
    } finally {
      setPayload(null);
    }
  }, []);

  const onTrafficDetected = useCallback((p) => {
    if (_readAuto()) {
      // Mode auto : accept silencieux immédiatement.
      acceptReroute("auto", p);
    } else {
      setPayload(p);
    }
  }, [acceptReroute]);

  const { snooze } = useTrafficWatchdog({
    enabled: !!activeCourse && activeCourse.status === "in_progress",
    destination,
    currentEtaMin: activeCourse?.eta_minutes || null,
    onTrafficDetected,
  });

  const onAccept = () => acceptReroute("manual");
  const onReject = async () => {
    const p = payloadRef.current;
    const ac = activeCourseRef.current;
    if (!p || !ac) { setPayload(null); return; }
    try {
      await axios.post(
        `${API}/v3/courier/courses/${ac.course_id}/log-reroute`,
        { saving_minutes: p.best.saving_minutes, accepted: false, source: "manual" },
        { withCredentials: true },
      );
    } catch (_) { /* ignore */ }
    snooze();
    setPayload(null);
  };

  if (!payload || autoMode) return null;
  return (
    <TrafficRerouteToast
      payload={payload}
      onAccept={onAccept}
      onReject={onReject}
      autoMode={autoMode}
    />
  );
}
